// client/src/components/pages/Settings/TenantDetails.tsx (converted from .jsx, ST-00222)
//
// Settings → Organization Details.
//   - IAM-13: changing the organisation's name and contact details needs tenant.edit
//     (PUT /api/tenants/current answers 403 without it); without it the form is read-only.
//   - ST-00224: settings.edit (Edit Settings: organisation settings) allows it too.
//   - "Created" reads created_at (it read CreatedAt, a SQL Server leftover, so it always
//     showed N/A).
//   - a failed save shows the server's `error` text (it read `message`, which the
//     tenants API never sends, and showed "Request failed with status code 400").
//   - IAM-15: the Users bar counts seats; the hint says what that means.
import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { FaSave, FaBuilding } from 'react-icons/fa';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import type {
  SettingsApiError,
  TenantDetailsRecord,
  TenantUsage,
} from '../../../types/clientPages/roleManagement';

interface Toast {
  message: string;
  type: 'success' | 'error';
}

// Usage bar helpers
const getUsagePercent = (current: number, max: number | null | undefined): number => {
  if (!max || max === 0) return 0;
  return Math.min(Math.round((current / max) * 100), 100);
};

const getBarColor = (percent: number): string => {
  if (percent >= 90) return 'bg-red-500';
  if (percent >= 70) return 'bg-yellow-500';
  return 'bg-emerald-500';
};

const getBarBg = (percent: number): string => {
  if (percent >= 90) return 'bg-red-100';
  if (percent >= 70) return 'bg-yellow-100';
  return 'bg-emerald-100';
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatStorage = (mb: number | null | undefined): string => {
  if (mb == null) return 'N/A';
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb} MB`;
};

const inputClass =
  'w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed';

const TenantDetails = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('tenant.edit') || hasPermission('settings.edit');

  // Data state
  const [tenant, setTenant] = useState<TenantDetailsRecord | null>(null);
  const [usage, setUsage] = useState<TenantUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Editable form fields
  const [tenantName, setTenantName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');

  // Save state
  const [saving, setSaving] = useState(false);

  // Toast message
  const [toast, setToast] = useState<Toast | null>(null);

  const showToast = useCallback((message: string, type: Toast['type'] = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [tenantRes, usageRes] = await Promise.all([
        api.tenants.getCurrent(),
        api.tenants.getUsage(),
      ]);

      const tenantBody = tenantRes.data as { success?: boolean; data?: TenantDetailsRecord };
      if (tenantBody?.success && tenantBody.data) {
        const t = tenantBody.data;
        setTenant(t);
        setTenantName(t.tenant_name || '');
        setContactEmail(t.contact_email || '');
        setContactPhone(t.contact_phone || '');
      } else {
        throw new Error('Failed to load tenant details.');
      }

      const usageBody = usageRes.data as { success?: boolean; data?: TenantUsage };
      if (usageBody?.success && usageBody.data) {
        setUsage(usageBody.data);
      }
    } catch (err) {
      console.error('Error fetching tenant data:', err);
      setError('Failed to load tenant details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const handleSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canEdit) return;

    if (!tenantName.trim()) {
      showToast('Organization name is required.', 'error');
      return;
    }

    try {
      setSaving(true);
      const response = await api.tenants.updateCurrent({
        tenantName: tenantName.trim(),
        contactEmail: contactEmail.trim(),
        contactPhone: contactPhone.trim(),
      });

      const body = response.data as { success?: boolean; error?: string; message?: string };
      if (body?.success) {
        setTenant((prev) =>
          prev
            ? {
                ...prev,
                tenant_name: tenantName.trim(),
                contact_email: contactEmail.trim(),
                contact_phone: contactPhone.trim(),
              }
            : prev
        );
        showToast('Organization details saved successfully.', 'success');
      } else {
        throw new Error(body?.error || body?.message || 'Save failed.');
      }
    } catch (err) {
      console.error('Error saving tenant details:', err);
      const apiErr = err as SettingsApiError;
      const msg =
        apiErr?.response?.data?.error ||
        apiErr?.response?.data?.message ||
        apiErr?.message ||
        'Failed to save. Please try again.';
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  // ---------- RENDER ----------

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="bg-surface border-b border-slate-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
              Organization Details
            </h1>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-primary-500"></div>
            <p className="mt-4 text-slate-600">Loading organization details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="bg-surface border-b border-slate-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
              Organization Details
            </h1>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="bg-surface rounded-xl border border-slate-200 shadow-sm p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
              <svg
                className="w-8 h-8 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">Failed to Load</h3>
            <p className="text-slate-600 mb-6 max-w-md mx-auto">{error}</p>
            <button
              onClick={() => void fetchData()}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const featureFlagEntries = Object.entries(tenant?.feature_flags || {});

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Toast notification */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div
            role={toast.type === 'error' ? 'alert' : 'status'}
            className={`flex items-center gap-3 px-5 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'
            }`}
          >
            {toast.type === 'success' ? (
              <svg
                className="w-5 h-5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            )}
            <span>{toast.message}</span>
            <button
              onClick={() => setToast(null)}
              aria-label="Dismiss"
              className="ml-2 opacity-70 hover:opacity-100 transition-opacity"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="sticky top-0 z-10 bg-surface border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary-100 text-primary-600">
              <FaBuilding className="text-lg" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                Organization Details
              </h1>
              <p className="text-slate-600 text-sm mt-0.5">
                View and manage your organization information and usage
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Editable Details Card */}
        <div className="bg-surface rounded-xl border border-slate-200 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-bold text-slate-900">Organization Information</h2>
            <p className="text-sm text-slate-600 mt-1">
              {canEdit
                ? 'Update your organization name and contact details'
                : 'Changing these details needs the Edit Tenant Details or Edit Settings permission.'}
            </p>
          </div>
          <form onSubmit={handleSave} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label
                  htmlFor="tenant-name"
                  className="block text-sm font-medium text-slate-700 mb-1.5"
                >
                  Organization Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="tenant-name"
                  type="text"
                  value={tenantName}
                  onChange={(e) => setTenantName(e.target.value)}
                  className={inputClass}
                  placeholder="Enter organization name"
                  disabled={!canEdit}
                  required
                />
              </div>
              <div>
                <label
                  htmlFor="tenant-contact-email"
                  className="block text-sm font-medium text-slate-700 mb-1.5"
                >
                  Contact Email
                </label>
                <input
                  id="tenant-contact-email"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  className={inputClass}
                  placeholder="admin@example.com"
                  disabled={!canEdit}
                />
              </div>
              <div>
                <label
                  htmlFor="tenant-contact-phone"
                  className="block text-sm font-medium text-slate-700 mb-1.5"
                >
                  Contact Phone
                </label>
                <input
                  id="tenant-contact-phone"
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  className={inputClass}
                  placeholder="+27 12 345 6789"
                  disabled={!canEdit}
                />
              </div>
            </div>
            {canEdit && (
              <div className="mt-6 flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-all duration-200 shadow-sm hover:shadow-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave className="text-sm" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            )}
          </form>
        </div>

        {/* Read-Only Info Card */}
        <div className="bg-surface rounded-xl border border-slate-200 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-bold text-slate-900">Subscription & Configuration</h2>
            <p className="text-sm text-slate-600 mt-1">
              Read-only details about your subscription and tenant configuration
            </p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                  Tenant Code
                </p>
                <p className="text-sm font-medium text-slate-900 font-mono">
                  {tenant?.tenant_code || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                  Subscription Tier
                </p>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary-100 text-primary-800">
                  {tenant?.subscription_tier || 'N/A'}
                </span>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                  Expiry Date
                </p>
                <p className="text-sm font-medium text-slate-900">
                  {formatDate(tenant?.expiry_date)}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                  Status
                </p>
                {tenant?.is_suspended ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                    Suspended
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                    Active
                  </span>
                )}
              </div>
            </div>

            {/* Additional read-only fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-6 pt-6 border-t border-slate-100">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                  Max Users
                </p>
                <p className="text-sm font-medium text-slate-900">{tenant?.max_users ?? 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                  Max Storage
                </p>
                <p className="text-sm font-medium text-slate-900">
                  {formatStorage(tenant?.max_storage_mb)}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                  Data Retention
                </p>
                <p className="text-sm font-medium text-slate-900">
                  {tenant?.data_retention_days ? `${tenant.data_retention_days} days` : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                  Created
                </p>
                <p className="text-sm font-medium text-slate-900">
                  {formatDate(tenant?.created_at)}
                </p>
              </div>
            </div>

            {/* Feature Flags */}
            {featureFlagEntries.length > 0 && (
              <div className="mt-6 pt-6 border-t border-slate-100">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                  Feature Flags
                </p>
                <div className="flex flex-wrap gap-2">
                  {featureFlagEntries.map(([key, value]) => (
                    <span
                      key={key}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        value
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-slate-100 text-slate-500 line-through'
                      }`}
                    >
                      {key}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Usage Card */}
        {usage && (
          <div className="bg-surface rounded-xl border border-slate-200 shadow-sm">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">Usage Overview</h2>
              <p className="text-sm text-slate-600 mt-1">
                Current resource utilization for your organization
              </p>
            </div>
            <div className="p-6 space-y-6">
              {/* Users usage (seats, IAM-15) */}
              {usage.users && (
                <UsageBar
                  label="Users"
                  current={usage.users.current}
                  max={usage.users.max}
                  hint="Active members. Lex-Adapt support staff do not use a seat."
                />
              )}

              {/* Storage usage */}
              {usage.storage && (
                <UsageBar
                  label="Storage"
                  current={usage.storage.usedMB}
                  max={usage.storage.maxMB}
                  formatValue={formatStorage}
                />
              )}

              {/* Resource counts */}
              {usage.resources && (
                <div className="pt-4 border-t border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                    Active Resources
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <ResourceStat label="Active Routes" value={usage.resources.activeRoutes} />
                    <ResourceStat
                      label="Custom Summaries"
                      value={usage.resources.customSummaries}
                    />
                    <ResourceStat label="Email Reports" value={usage.resources.emailReports} />
                    <ResourceStat label="Custom Roles" value={usage.resources.customRoles} />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Sub-components ----------

interface UsageBarProps {
  label: string;
  current: number;
  max: number | null;
  formatValue?: (n: number | null) => string;
  hint?: string;
}

const UsageBar = ({ label, current, max, formatValue, hint }: UsageBarProps) => {
  const percent = getUsagePercent(current, max);
  const displayCurrent = formatValue ? formatValue(current) : current;
  const displayMax = formatValue ? formatValue(max) : (max ?? 'N/A');

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        <span className="text-sm text-slate-600">
          {displayCurrent} / {displayMax} <span className="text-slate-400">({percent}%)</span>
        </span>
      </div>
      <div className={`w-full h-2.5 rounded-full ${getBarBg(percent)}`}>
        <div
          className={`h-2.5 rounded-full transition-all duration-500 ${getBarColor(percent)}`}
          style={{ width: `${percent}%` }}
        />
      </div>
      {hint && <p className="text-xs text-slate-500 mt-1.5">{hint}</p>}
    </div>
  );
};

const ResourceStat = ({ label, value }: { label: string; value: number | null | undefined }) => (
  <div className="bg-slate-50 rounded-lg p-3 text-center">
    <p className="text-2xl font-bold text-slate-900">{value ?? 0}</p>
    <p className="text-xs text-slate-500 mt-0.5">{label}</p>
  </div>
);

export default TenantDetails;
